import React,{useEffect,useRef,useState} from 'react';
import {FiArrowRight,FiCheck,FiChevronDown,FiDownloadCloud,FiExternalLink,FiFolder,FiInfo,FiLink2,FiX} from 'react-icons/fi';
import {googleConfigured,dropboxConfigured,prepareGooglePicker,
 chooseGoogleVideo,prepareDropboxChooser,chooseDropboxVideo,stageCloudLink,safeVideoName,cloudImportLimits} from '../lib/cloudImport';
import {MAX_MEDIA_BYTES,mediaLimitLabel} from '../lib/resumableUpload';
import CloudProviderLogo from './CloudProviderLogo';
import './CloudImportDialog.css';

const PROVIDERS=[
 {id:'google',name:'Google Drive',placeholder:'https://drive.google.com/file/d/…'},
 {id:'dropbox',name:'Dropbox',placeholder:'https://www.dropbox.com/scl/fi/…'},
 {id:'frameio',name:'Frame.io',placeholder:'https://…/download/…'}
];
const nameOf=id=>PROVIDERS.find(p=>p.id===id)?.name||'Cloud';
const progressText=(i,count,name)=>count>1?`Downloading ${i} of ${count} · ${name}`:`Downloading · ${name}`;
const transferKey=file=>[file.provider,file.url,file.filename,file.bytes].join('|');
const sizeLabel=bytes=>bytes>=1e9?(bytes/1e9).toFixed(1)+' GB':(bytes/1e6).toFixed(1)+' MB';

export default function CloudImportDialog({onClose,onImport,getAccessToken}){
 const [provider,setProvider]=useState('google');
 const [ready,setReady]=useState({google:false,dropbox:false});
 const [pickerErrors,setPickerErrors]=useState({});
 const [pickerFiles,setPickerFiles]=useState([]);
 const [mode,setMode]=useState('link');
 const [url,setUrl]=useState('');
 const [filename,setFilename]=useState('');
 const [error,setError]=useState('');
 const [progress,setProgress]=useState('');
 const [percent,setPercent]=useState(null);
 const [transferring,setTransferring]=useState(false);
 const transferRef=useRef(null),staged=useRef(new Map()),handedOff=useRef(false);
 const configured=provider==='google'?googleConfigured():provider==='dropbox'?dropboxConfigured():false;
 const pickerReady=Boolean(ready[provider]);
 const item=PROVIDERS.find(p=>p.id===provider);
 const linkValid=Boolean(url.trim()&&safeVideoName(filename.trim()));
 const canImport=mode==='picker'?pickerFiles.length>0:linkValid;
 const items=mode==='picker'?pickerFiles:[{
  provider:provider==='google'?'google-link':provider,url:url.trim(),filename:filename.trim(),bytes:null
 }];
 const label=mode==='picker'
  ?`Import ${pickerFiles.length} interview${pickerFiles.length===1?'':'s'}`
  :'Import video';

 useEffect(()=>{
  let active=true;
  if(googleConfigured())prepareGooglePicker().then(()=>{
   if(active)setReady(v=>({...v,google:true}));
  }).catch(e=>{if(active)setPickerErrors(v=>({...v,google:e.message||'Could not load Google Drive picker.'}));});
  if(dropboxConfigured())prepareDropboxChooser().then(()=>{
   if(active)setReady(v=>({...v,dropbox:true}));
  }).catch(e=>{if(active)setPickerErrors(v=>({...v,dropbox:e.message||'Could not load Dropbox picker.'}));});
  return()=>{active=false;};
 },[]);
 const releaseStaged=async()=>{
  const pending=[...staged.current.values()];
  staged.current.clear();
  await Promise.allSettled(pending.map(item=>item.cleanup()));
 };
 const dismiss=()=>{
  if(transferring){transferRef.current?.abort();return;}
  void releaseStaged();
  onClose();
 };
 useEffect(()=>{
  const escape=e=>{
   if(e.key==='Escape'){e.stopPropagation();if(transferRef.current)transferRef.current.abort();
    else{void releaseStaged();onClose();}}
  };
  document.addEventListener('keydown',escape);
  return()=>document.removeEventListener('keydown',escape);
 },[onClose]);
 useEffect(()=>()=>{transferRef.current?.abort();
  if(!handedOff.current)void releaseStaged();
 },[]);

 const switchProvider=next=>{
  if(transferring||next===provider)return;
  void releaseStaged();
  setProvider(next);setPickerFiles([]);setMode('link');
  setUrl('');setFilename('');setError('');setProgress('');setPercent(null);
 };
 const setPicked=chosen=>{
  if(!chosen?.length)return;
  if(chosen.length>10){setError('Choose no more than 10 files at once.');return;}
  if(chosen.some(file=>!safeVideoName(file.filename)||
    (file.bytes!=null&&Number(file.bytes)>MAX_MEDIA_BYTES))){
   setError(`This video is unsupported or exceeds the current ${mediaLimitLabel(MAX_MEDIA_BYTES)} import limit.`);return;
  }
  void releaseStaged();
  setPickerFiles(chosen);setMode('picker');setError('');setProgress('');setPercent(null);
 };
 const openPicker=()=>{
  try{
   if(provider==='google'){
    chooseGoogleVideo({onChosen:setPicked,onError:e=>setError(e.message)});
   }else if(provider==='dropbox'){
    chooseDropboxVideo({onChosen:setPicked});
   }
  }catch(e){setError(e.message);}
 };
 const updateUrl=value=>{
  void releaseStaged();
  setMode('link');setPickerFiles([]);setUrl(value);setError('');setPercent(null);setProgress('');
  if(!filename.trim()){
   try{
    const name=decodeURIComponent(new URL(value).pathname.split('/').pop()||'');
    if(safeVideoName(name))setFilename(name);
   }catch{ /* Partial links are normal while typing. */ }
  }
 };
 const start=async()=>{
  if(transferring)return;
  if(!canImport){
   setError('Add a downloadable video link and its original filename (for example interview.mp4).');return;
  }
  const controller=new AbortController();
  transferRef.current=controller;
  setTransferring(true);setError('');setPercent(null);
  const queued=[...items],completed=[];
  try{
   for(let i=0;i<queued.length;i++){
    if(controller.signal.aborted)throw new DOMException('Import cancelled.','AbortError');
    const current=queued[i],key=transferKey(current);
    let stage=staged.current.get(key);
    setProgress(progressText(i+1,queued.length,current.filename));
    setPercent(stage?100:null);
    if(!stage){
     stage=await stageCloudLink({...current,
      accessToken:current.provider==='google'?current.accessToken:getAccessToken,
      signal:controller.signal,
      onProgress:value=>{
       if(controller.signal.aborted)return;
       setPercent(value.percent);
       if(value.route==='relay')setProgress(progressText(i+1,queued.length,current.filename)+' · secure relay');
       else if(value.phase==='connecting')setProgress('Connecting to '+nameOf(provider)+'…');
       else setProgress(progressText(i+1,queued.length,current.filename)+
        (value.bytes?' · '+sizeLabel(value.bytes):''));
      }
     });
     staged.current.set(key,stage);
    }
    completed.push(stage.file);
   }
   setProgress('Ready · adding interviews to your project');
   setPercent(100);
   // Hand the disk-backed files to the existing validated upload/transcription
   // queue, then close this small picker. Keep temporary files until it finishes.
   handedOff.current=true;
   const task=Promise.resolve().then(()=>onImport(completed));
   onClose();
   void task.catch(()=>{/* The project batch reports its own failed items. */})
    .finally(()=>releaseStaged());
  }catch(e){
   if(controller.signal.aborted)setError('Download cancelled. Already downloaded files are kept here for retry.');
   else setError((e.message||'Cloud download failed.')+' Previously downloaded files will not be fetched again.');
   setProgress('');setPercent(null);
  }finally{transferRef.current=null;setTransferring(false);}
 };
 const pickerEnabled=configured&&pickerReady;
 const pickerUnavailable=provider==='frameio'
  ?'Frame.io project browsing is not connected yet. Use a signed original or proxy download URL.'
  :!configured
   ?`${nameOf(provider)} account browsing is not enabled on this Takefold deployment. Shared download links are still supported below.`
   :pickerErrors[provider]||null;
 const providerStatus=id=>id==='frameio'?'Signed link':
  (id==='google'?googleConfigured():dropboxConfigured())?'Browse files':'Link only';
 return <div className="modal-backdrop cloud-import-overlay">
  <section className="modal cloud-import-dialog" role="dialog" aria-modal="true"
   aria-labelledby="cloud-import-title" aria-describedby="cloud-import-subtitle">
   <button type="button" className="cloud-import-close" onClick={dismiss}
    aria-label={transferring?'Cancel cloud download':'Close import dialog'}><FiX aria-hidden="true"/></button>

   <header className="cloud-import-head">
    <div className="cloud-import-head-icon"><FiDownloadCloud aria-hidden="true"/></div>
    <div>
     <h2 id="cloud-import-title">Import from cloud</h2>
     <p id="cloud-import-subtitle">Browse supported accounts or import an MP4/WebM download link.</p>
    </div>
   </header>

   <div className="cloud-import-providers" role="tablist" aria-label="Choose cloud provider">
    {PROVIDERS.map(option=><button type="button" role="tab" key={option.id}
     className={provider===option.id?'is-active':''}
     id={`cloud-provider-${option.id}`} aria-controls="cloud-import-provider-panel"
     aria-selected={provider===option.id} disabled={transferring}
     onClick={()=>switchProvider(option.id)}>
     <CloudProviderLogo provider={option.id} size={27}/>
     <span>{option.name}</span>
     <small className="cloud-provider-capability">{providerStatus(option.id)}</small>
     {provider===option.id&&<FiCheck className="cloud-import-selected-icon" aria-hidden="true"/>}
    </button>)}
   </div>

   <div id="cloud-import-provider-panel" className="cloud-import-body" role="tabpanel"
    aria-labelledby={`cloud-provider-${provider}`}>
    {mode==='picker'&&pickerFiles.length>0
     ?<div className="cloud-import-picked">
       <div className="cloud-import-picked-heading">
        <strong>{pickerFiles.length} video{pickerFiles.length===1?'':'s'} selected</strong>
        <button type="button" disabled={transferring} onClick={()=>{
         void releaseStaged();setMode('link');setPickerFiles([]);
        }}>Use link instead</button>
       </div>
       <ul>{pickerFiles.map((file,index)=><li key={`${file.filename}-${index}`}>
        <FiCheck aria-hidden="true"/>
        <span title={file.filename}>{file.filename}</span>
        {file.bytes!=null&&<small>{sizeLabel(Number(file.bytes))}</small>}
        {staged.current.has(transferKey(file))&&<small className="cloud-import-done">Downloaded</small>}
       </li>)}</ul>
      </div>
     :<>
       <div className="cloud-import-provider-access" role="status">
        {pickerEnabled?<div className="cloud-import-browse">
         <button type="button" onClick={openPicker} disabled={transferring}>
          <FiFolder aria-hidden="true"/> Browse files in {nameOf(provider)}
          <FiArrowRight aria-hidden="true"/>
         </button>
         <span className="cloud-import-connection-detail"><FiCheck aria-hidden="true"/> Account picker available</span>
        </div>:configured&&provider!=='frameio'&&!pickerUnavailable
         ?<p className="cloud-import-connection-detail"><FiInfo aria-hidden="true"/> Preparing {nameOf(provider)} file picker…</p>
         :<p className="cloud-import-provider-notice"><FiInfo aria-hidden="true"/> {pickerUnavailable}</p>}
       </div>
       {pickerEnabled&&<div className="cloud-import-or"><span>or import a shared link</span></div>}
       <div className="cloud-import-fields">
        <label htmlFor="cloud-import-url">{provider==='frameio'?'Signed media download URL':'Cloud video share link'}</label>
        <div className="cloud-import-url-wrap">
         <FiLink2 aria-hidden="true"/>
         <input id="cloud-import-url" type="url" autoComplete="off" spellCheck="false"
          placeholder={item.placeholder} value={url} disabled={transferring}
          onChange={e=>updateUrl(e.target.value)}/>
        </div>
        <label htmlFor="cloud-import-filename">Original filename
         <FiInfo aria-label="Used to relink original files in Premiere, Resolve or Final Cut."
          title="Used to relink original files in Premiere, Resolve or Final Cut."/>
        </label>
        <input id="cloud-import-filename" type="text" maxLength={180} autoComplete="off"
         placeholder="interview-01.mp4" value={filename} disabled={transferring}
         onChange={e=>{void releaseStaged();setMode('link');setFilename(e.target.value);setError('');setProgress('');}}
         onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();void start();}}}/>
       </div>
      </>}

    <details className="cloud-import-hint" key={provider}>
     <summary><FiInfo aria-hidden="true"/> Import details <FiChevronDown aria-hidden="true"/></summary>
     {provider==='google'&&<p>Browse with your Google account to import private MP4/WebM files without changing sharing permissions. Share links must allow downloading. Direct transfer supports up to {mediaLimitLabel(MAX_MEDIA_BYTES)} with sufficient device storage; secure link fallback is {mediaLimitLabel(cloudImportLimits.relay)}.</p>}
     {provider==='dropbox'&&<p>Browse Dropbox or paste a file share link with downloads enabled. Direct transfers support {mediaLimitLabel(MAX_MEDIA_BYTES)} when the provider permits browser downloads; the secure fallback supports {mediaLimitLabel(cloudImportLimits.relay)}. Source files stay in Dropbox.</p>}
     {provider==='frameio'&&<p>Use an unexpired signed video download URL, not a review page. Direct transfers support {mediaLimitLabel(MAX_MEDIA_BYTES)} when permitted by Frame.io CORS; secure relay is limited to {mediaLimitLabel(cloudImportLimits.relay)}. <a href="https://developer.frame.io/platform/v4/docs/guides/media-links" target="_blank" rel="noopener noreferrer">Frame.io media links <FiExternalLink aria-hidden="true"/></a></p>}
    </details>
   </div>

   {progress&&<div className="cloud-import-transfer" role="status" aria-live="polite">
    <span>{progress}</span><strong>{percent!=null?`${percent}%`:'Downloading'}</strong>
    <div className={'cloud-import-track'+(percent===null?' is-indeterminate':'')}>
     <div style={percent===null?undefined:{width:`${percent}%`}}/>
    </div>
   </div>}
   {error&&<p className="cloud-import-error" role="alert">{error}</p>}
   <footer className="cloud-import-footer">
    <span className="cloud-import-limit"><FiInfo aria-hidden="true"/> Up to {mediaLimitLabel(cloudImportLimits.direct)} direct · {mediaLimitLabel(cloudImportLimits.relay)} secure fallback · Original filename kept</span>
    <div className="cloud-import-actions">
     <button type="button" className="cloud-import-cancel" onClick={dismiss}>{transferring?'Cancel download':'Cancel'}</button>
     <button type="button" className="cloud-import-submit" disabled={!canImport||transferring}
      onClick={start}>{transferring?'Downloading…':label}<FiArrowRight aria-hidden="true"/></button>
    </div>
   </footer>
  </section>
 </div>;
}
